/**
 * Position de la cote d'un segment (PUR, testé).
 *
 * Avant : la cote était posée au milieu du segment, décalée vers le HAUT
 * seulement — sur un segment vertical elle tombait sur le trait. Elle est
 * maintenant décalée PERPENDICULAIREMENT au segment, du côté opposé au centre
 * de la figure (vers l'extérieur), et alignée selon ce côté (à gauche / à
 * droite / au-dessus / en dessous) pour ne jamais chevaucher le trait.
 */

export interface Pt {
  x: number;
  y: number;
}

export interface SegmentLabelPlacement {
  x: number;
  y: number;
  textAnchor: 'start' | 'middle' | 'end';
  /** Décalage vertical du texte (em) pour le centrer sur sa position. */
  dy: string;
}

export function segmentLabelPlacement(
  a: Pt,
  b: Pt,
  center: Pt | null,
  offset: number,
): SegmentLabelPlacement {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return { x: mx, y: my - offset, textAnchor: 'middle', dy: '0' };
  // Normale unitaire ; par défaut vers le haut (y écran vers le bas) ou la gauche.
  let nx = -dy / len;
  let ny = dx / len;
  if (center) {
    // Du côté OPPOSÉ au centre de la figure.
    if ((center.x - mx) * nx + (center.y - my) * ny > 0) {
      nx = -nx;
      ny = -ny;
    }
  } else if (ny > 0 || (Math.abs(ny) < 1e-9 && nx > 0)) {
    nx = -nx;
    ny = -ny;
  }
  const x = mx + nx * offset;
  const y = my + ny * offset;
  const textAnchor = nx > 0.35 ? 'start' : nx < -0.35 ? 'end' : 'middle';
  // Au-dessus : la ligne de base est au point ; en dessous : on descend d'une ligne ;
  // sur le côté : centré verticalement.
  const textDy = ny < -0.35 ? '0' : ny > 0.35 ? '0.8em' : '0.35em';
  return { x, y, textAnchor, dy: textDy };
}

/** Centre (moyenne) des points de la figure, ou null s'il n'y en a pas. */
export function figureCenter(points: readonly Pt[]): Pt | null {
  if (points.length === 0) return null;
  const s = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  return { x: s.x / points.length, y: s.y / points.length };
}

/**
 * Position du nom d'un point : à l'EXTÉRIEUR de la figure (direction centre → point),
 * à `offset` du point, pour ne jamais tomber sur un trait. Sans centre (point seul) :
 * en haut à droite. PUR.
 */
export function pointNamePlacement(p: Pt, center: Pt | null, offset = 13): SegmentLabelPlacement {
  let ux = 0.7071;
  let uy = -0.7071;
  if (center) {
    const dx = p.x - center.x;
    const dy = p.y - center.y;
    const len = Math.hypot(dx, dy);
    if (len > 1e-6) {
      ux = dx / len;
      uy = dy / len;
    }
  }
  const x = p.x + ux * offset;
  const y = p.y + uy * offset;
  const textAnchor = ux > 0.35 ? 'start' : ux < -0.35 ? 'end' : 'middle';
  const dy = uy < -0.35 ? '0' : uy > 0.35 ? '0.8em' : '0.35em';
  return { x, y, textAnchor, dy };
}
